import { CartItemType } from "@workspace/crystallize/generated/shop/cart/";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { FormattedPrice } from "@workspace/ui/components/formatted-price";
import { Separator } from "@workspace/ui/components/separator";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@workspace/ui/components/sheet";
import { cn } from "@workspace/ui/lib/utils";
import { Loader2, ShoppingBag, ShoppingCart } from "lucide-react";
import { NavLink } from "react-router";
import { CartItem } from "./cart-item";
import { useCart } from "./cart-provider";

export default function Cart() {
	const { cart, isOpen, setIsOpen, updateCart, toggleCart } = useCart();

	const allItems = cart?.items ?? [];
	const cartItems = allItems.filter((i) => i.type !== CartItemType.Shipping);
	const shippingItems = allItems.filter((i) => i.type === CartItemType.Shipping);
	return (
		<Sheet open={isOpen} onOpenChange={setIsOpen}>
			<SheetTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					aria-label="Åpne handlekurv"
					className={cn("h-8 w-8", cartItems.length > 0 && "w-16")}
				>
					<ShoppingCart className="size-4" />
									{cartItems.length > 0 && (
						<span className="flex size-5 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground text-xs">
							{cartItems.reduce((sum: number, item) => sum + item.quantity, 0)}
						</span>
					)}
				</Button>
			</SheetTrigger>
			<SheetContent className="flex w-full flex-col px-4 sm:max-w-lg">
				<SheetHeader>
					<SheetTitle>Handlekurv</SheetTitle>
				</SheetHeader>
				<div className="mt-8 flex-1 space-y-4 overflow-y-auto">
					{allItems.length === 0 ? (
						<p className="text-center text-gray-500">Din handlekurv er tom</p>
					) : (
						cartItems.map((item) => (
							<CartItem
								key={item.variant.sku}
								item={item}
								updateCart={updateCart}
							/>
						))
					)}
				</div>
				{allItems.length > 0 && (
					<div className="my-8 space-y-4">
						{cart?.total.discounts && cart?.total.discounts.length > 0 && (
							<>
								<Separator />
								<div className="flex items-center justify-between gap-4 text-green-600">
									<span>Rabatt</span>
									<div className="flex gap-2">
										{cart?.appliedPromotions?.map((promotion) => (
											<div key={promotion.identifier}>
												<Badge
													variant="secondary"
													className="ml-2 border-green-200 bg-green-50 px-2 text-green-600"
												>
													{promotion.name} (-
													{promotion.mechanism?.type === "Percentage" ? (
														`${promotion.mechanism?.value}%`
													) : (
														<FormattedPrice
															price={promotion.mechanism?.value ?? 0}
														/>
													)}
													)
												</Badge>
											</div>
										))}

										<FormattedPrice
											price={
												(cart?.total.discounts.reduce(
													(sum: number, discount) => sum + discount.amount,
													0,
												) || 0) * -1
											}
											className="font-semibold text-green-600 text-lg"
										/>
									</div>
								</div>
							</>
						)}
						<Separator />

						{shippingItems.map((item) => (
							<div key={item.variant.sku} className="flex items-center justify-between gap-4 text-sm">
								<span>{item.name}</span>
								<FormattedPrice price={item.price.gross} />
							</div>
						))}

						<div className="flex items-center justify-between gap-4">
							<span>Totalt</span>
							<FormattedPrice price={cart?.total.gross || 0} />
						</div>
						<Button className="w-full" size="lg" asChild>
							<NavLink to="/kasse/privat" prefetch="intent" className="w-full">
								{({ isActive, isPending, isTransitioning }) => (
									<>
										{isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Gå til kassen
									</>
								)}
							</NavLink>
						</Button>

						<Button
							variant="outline"
							size="lg"
							className="w-full"
							onClick={() => {
								setIsOpen(false);
							}}
						>
							<ShoppingBag size={16} className="mr-1" />
							Fortsett å handle
						</Button>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
}
